import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Line, Area } from 'recharts';
import { ArrowRight, AlertTriangle, CheckCircle, FileText, DollarSign, Percent, BarChart3, Coins, ChevronDown } from 'lucide-react';
interface CaptiveCellPerformanceProps {
  onNavigate?: (dashboard: string) => void;
}
// Cell definitions
interface CellData {
  id: string;
  name: string;
  category: 'Fleet Motor' | 'Property';
  sponsor: string;
  type: string;
  currency: string;
  capitalStatus: 'OK' | 'Watch' | 'Breach';
  uwResult: string;
  uwResultPositive: boolean;
  capitalUtilisation: number;
  lrVolatility: number;
  dividendCapacity: number;
  lossTriangle: {
    year: string;
    paid: number;
    incurred: number;
    ultimate: number;
  }[];
  capitalBuffer: {
    month: string;
    required: number;
    available: number;
  }[];
  claimsSummary: {
    type: string;
    count: number;
    value: string;
  }[];
  underwritingExceptions: {
    id: number;
    type: string;
    count: number;
    status: string;
  }[];
}
const cells: CellData[] = [{
  id: 'ubuntu-fleet',
  name: 'Ubuntu Fleet Cell',
  category: 'Fleet Motor',
  sponsor: 'Ubuntu Collective Ltd',
  type: 'Fleet Programme',
  currency: 'ZAR',
  capitalStatus: 'OK',
  uwResult: '+R18.2M',
  uwResultPositive: true,
  capitalUtilisation: 68.4,
  lrVolatility: 3.2,
  dividendCapacity: 4.5,
  lossTriangle: [{
    year: '2020',
    paid: 82,
    incurred: 90,
    ultimate: 93
  }, {
    year: '2021',
    paid: 68,
    incurred: 82,
    ultimate: 86
  }, {
    year: '2022',
    paid: 42,
    incurred: 72,
    ultimate: 80
  }, {
    year: '2023',
    paid: 22,
    incurred: 60,
    ultimate: 74
  }, {
    year: '2024',
    paid: 8,
    incurred: 40,
    ultimate: 62
  }],
  capitalBuffer: [{
    month: 'Q1',
    required: 100,
    available: 160
  }, {
    month: 'Q2',
    required: 105,
    available: 165
  }, {
    month: 'Q3',
    required: 108,
    available: 162
  }, {
    month: 'Q4',
    required: 112,
    available: 158
  }],
  claimsSummary: [{
    type: 'Open',
    count: 98,
    value: 'R62M'
  }, {
    type: 'Large (>R1.5M)',
    count: 3,
    value: 'R12M'
  }, {
    type: 'Litigated',
    count: 7,
    value: 'R8.5M'
  }],
  underwritingExceptions: [{
    id: 1,
    type: 'Rate Deviation',
    count: 2,
    status: 'Approved'
  }, {
    id: 2,
    type: 'Referral Trigger',
    count: 5,
    status: 'Review'
  }, {
    id: 3,
    type: 'Limit Breach',
    count: 0,
    status: 'OK'
  }]
}, {
  id: 'highveld-logistics',
  name: 'Highveld Logistics Cell',
  category: 'Fleet Motor',
  sponsor: 'Highveld Distribution Group',
  type: 'Fleet Programme',
  currency: 'ZAR',
  capitalStatus: 'Watch',
  uwResult: '-R2.1M',
  uwResultPositive: false,
  capitalUtilisation: 84.2,
  lrVolatility: 6.8,
  dividendCapacity: 1.2,
  lossTriangle: [{
    year: '2020',
    paid: 88,
    incurred: 95,
    ultimate: 98
  }, {
    year: '2021',
    paid: 75,
    incurred: 88,
    ultimate: 92
  }, {
    year: '2022',
    paid: 52,
    incurred: 80,
    ultimate: 88
  }, {
    year: '2023',
    paid: 30,
    incurred: 72,
    ultimate: 85
  }, {
    year: '2024',
    paid: 15,
    incurred: 55,
    ultimate: 72
  }],
  capitalBuffer: [{
    month: 'Q1',
    required: 100,
    available: 130
  }, {
    month: 'Q2',
    required: 108,
    available: 128
  }, {
    month: 'Q3',
    required: 115,
    available: 125
  }, {
    month: 'Q4',
    required: 120,
    available: 122
  }],
  claimsSummary: [{
    type: 'Open',
    count: 186,
    value: 'R95M'
  }, {
    type: 'Large (>R1.5M)',
    count: 7,
    value: 'R28M'
  }, {
    type: 'Litigated',
    count: 15,
    value: 'R18M'
  }],
  underwritingExceptions: [{
    id: 1,
    type: 'Rate Deviation',
    count: 6,
    status: 'Review'
  }, {
    id: 2,
    type: 'Referral Trigger',
    count: 12,
    status: 'Review'
  }, {
    id: 3,
    type: 'Limit Breach',
    count: 2,
    status: 'Open'
  }]
}, {
  id: 'imbokodo-mobility',
  name: 'Imbokodo Mobility Cell',
  category: 'Fleet Motor',
  sponsor: 'Imbokodo Services',
  type: 'Fleet Programme',
  currency: 'ZAR',
  capitalStatus: 'OK',
  uwResult: '+R8.6M',
  uwResultPositive: true,
  capitalUtilisation: 72.1,
  lrVolatility: 4.1,
  dividendCapacity: 3.0,
  lossTriangle: [{
    year: '2020',
    paid: 80,
    incurred: 88,
    ultimate: 91
  }, {
    year: '2021',
    paid: 65,
    incurred: 80,
    ultimate: 84
  }, {
    year: '2022',
    paid: 40,
    incurred: 70,
    ultimate: 78
  }, {
    year: '2023',
    paid: 20,
    incurred: 58,
    ultimate: 72
  }, {
    year: '2024',
    paid: 6,
    incurred: 38,
    ultimate: 58
  }],
  capitalBuffer: [{
    month: 'Q1',
    required: 80,
    available: 120
  }, {
    month: 'Q2',
    required: 82,
    available: 125
  }, {
    month: 'Q3',
    required: 85,
    available: 122
  }, {
    month: 'Q4',
    required: 88,
    available: 118
  }],
  claimsSummary: [{
    type: 'Open',
    count: 64,
    value: 'R38M'
  }, {
    type: 'Large (>R1.5M)',
    count: 2,
    value: 'R6M'
  }, {
    type: 'Litigated',
    count: 4,
    value: 'R5M'
  }],
  underwritingExceptions: [{
    id: 1,
    type: 'Rate Deviation',
    count: 1,
    status: 'Approved'
  }, {
    id: 2,
    type: 'Referral Trigger',
    count: 3,
    status: 'OK'
  }, {
    id: 3,
    type: 'Limit Breach',
    count: 0,
    status: 'OK'
  }]
}, {
  id: 'baobab-transport',
  name: 'Baobab Transport Cell',
  category: 'Fleet Motor',
  sponsor: 'Baobab Holdings',
  type: 'Fleet Programme',
  currency: 'ZAR',
  capitalStatus: 'OK',
  uwResult: '+R22.4M',
  uwResultPositive: true,
  capitalUtilisation: 61.5,
  lrVolatility: 2.9,
  dividendCapacity: 6.8,
  lossTriangle: [{
    year: '2020',
    paid: 78,
    incurred: 85,
    ultimate: 88
  }, {
    year: '2021',
    paid: 62,
    incurred: 78,
    ultimate: 82
  }, {
    year: '2022',
    paid: 38,
    incurred: 65,
    ultimate: 74
  }, {
    year: '2023',
    paid: 18,
    incurred: 52,
    ultimate: 68
  }, {
    year: '2024',
    paid: 5,
    incurred: 32,
    ultimate: 55
  }],
  capitalBuffer: [{
    month: 'Q1',
    required: 120,
    available: 200
  }, {
    month: 'Q2',
    required: 125,
    available: 210
  }, {
    month: 'Q3',
    required: 128,
    available: 205
  }, {
    month: 'Q4',
    required: 132,
    available: 198
  }],
  claimsSummary: [{
    type: 'Open',
    count: 210,
    value: 'R105M'
  }, {
    type: 'Large (>R1.5M)',
    count: 4,
    value: 'R15M'
  }, {
    type: 'Litigated',
    count: 9,
    value: 'R11M'
  }],
  underwritingExceptions: [{
    id: 1,
    type: 'Rate Deviation',
    count: 1,
    status: 'Approved'
  }, {
    id: 2,
    type: 'Referral Trigger',
    count: 4,
    status: 'Review'
  }, {
    id: 3,
    type: 'Limit Breach',
    count: 0,
    status: 'OK'
  }]
}, {
  id: 'karoo-property',
  name: 'Karoo Commercial Property Cell',
  category: 'Property',
  sponsor: 'Karoo Agri Holdings',
  type: 'Property Programme',
  currency: 'ZAR',
  capitalStatus: 'OK',
  uwResult: '+R5.1M',
  uwResultPositive: true,
  capitalUtilisation: 70.3,
  lrVolatility: 5.5,
  dividendCapacity: 2.4,
  lossTriangle: [{
    year: '2020',
    paid: 75,
    incurred: 84,
    ultimate: 88
  }, {
    year: '2021',
    paid: 60,
    incurred: 76,
    ultimate: 82
  }, {
    year: '2022',
    paid: 35,
    incurred: 62,
    ultimate: 72
  }, {
    year: '2023',
    paid: 15,
    incurred: 48,
    ultimate: 65
  }, {
    year: '2024',
    paid: 4,
    incurred: 28,
    ultimate: 50
  }],
  capitalBuffer: [{
    month: 'Q1',
    required: 60,
    available: 90
  }, {
    month: 'Q2',
    required: 62,
    available: 92
  }, {
    month: 'Q3',
    required: 65,
    available: 88
  }, {
    month: 'Q4',
    required: 68,
    available: 85
  }],
  claimsSummary: [{
    type: 'Open',
    count: 32,
    value: 'R18M'
  }, {
    type: 'Large (>R1.5M)',
    count: 1,
    value: 'R3M'
  }, {
    type: 'Litigated',
    count: 2,
    value: 'R2.5M'
  }],
  underwritingExceptions: [{
    id: 1,
    type: 'Rate Deviation',
    count: 0,
    status: 'OK'
  }, {
    id: 2,
    type: 'Referral Trigger',
    count: 2,
    status: 'Review'
  }, {
    id: 3,
    type: 'Limit Breach',
    count: 0,
    status: 'OK'
  }]
}, {
  id: 'drakensberg-asset',
  name: 'Drakensberg Asset Protection Cell',
  category: 'Property',
  sponsor: 'Drakensberg Property Trust',
  type: 'Property Programme',
  currency: 'ZAR',
  capitalStatus: 'OK',
  uwResult: '+R9.8M',
  uwResultPositive: true,
  capitalUtilisation: 65.8,
  lrVolatility: 3.8,
  dividendCapacity: 3.6,
  lossTriangle: [{
    year: '2020',
    paid: 72,
    incurred: 82,
    ultimate: 86
  }, {
    year: '2021',
    paid: 58,
    incurred: 74,
    ultimate: 80
  }, {
    year: '2022',
    paid: 32,
    incurred: 60,
    ultimate: 70
  }, {
    year: '2023',
    paid: 14,
    incurred: 45,
    ultimate: 62
  }, {
    year: '2024',
    paid: 3,
    incurred: 25,
    ultimate: 48
  }],
  capitalBuffer: [{
    month: 'Q1',
    required: 75,
    available: 120
  }, {
    month: 'Q2',
    required: 78,
    available: 125
  }, {
    month: 'Q3',
    required: 80,
    available: 122
  }, {
    month: 'Q4',
    required: 82,
    available: 118
  }],
  claimsSummary: [{
    type: 'Open',
    count: 45,
    value: 'R25M'
  }, {
    type: 'Large (>R1.5M)',
    count: 2,
    value: 'R7M'
  }, {
    type: 'Litigated',
    count: 3,
    value: 'R4M'
  }],
  underwritingExceptions: [{
    id: 1,
    type: 'Rate Deviation',
    count: 1,
    status: 'Approved'
  }, {
    id: 2,
    type: 'Referral Trigger',
    count: 1,
    status: 'OK'
  }, {
    id: 3,
    type: 'Limit Breach',
    count: 0,
    status: 'OK'
  }]
}, {
  id: 'mzansi-business',
  name: 'Mzansi Business Property Cell',
  category: 'Property',
  sponsor: 'Mzansi SME Alliance',
  type: 'Property Programme',
  currency: 'ZAR',
  capitalStatus: 'Watch',
  uwResult: '-R0.8M',
  uwResultPositive: false,
  capitalUtilisation: 82.1,
  lrVolatility: 7.2,
  dividendCapacity: 0.6,
  lossTriangle: [{
    year: '2020',
    paid: 85,
    incurred: 92,
    ultimate: 96
  }, {
    year: '2021',
    paid: 72,
    incurred: 86,
    ultimate: 90
  }, {
    year: '2022',
    paid: 48,
    incurred: 78,
    ultimate: 85
  }, {
    year: '2023',
    paid: 28,
    incurred: 68,
    ultimate: 80
  }, {
    year: '2024',
    paid: 12,
    incurred: 50,
    ultimate: 68
  }],
  capitalBuffer: [{
    month: 'Q1',
    required: 50,
    available: 65
  }, {
    month: 'Q2',
    required: 55,
    available: 64
  }, {
    month: 'Q3',
    required: 58,
    available: 62
  }, {
    month: 'Q4',
    required: 60,
    available: 60
  }],
  claimsSummary: [{
    type: 'Open',
    count: 78,
    value: 'R42M'
  }, {
    type: 'Large (>R1.5M)',
    count: 4,
    value: 'R14M'
  }, {
    type: 'Litigated',
    count: 8,
    value: 'R9M'
  }],
  underwritingExceptions: [{
    id: 1,
    type: 'Rate Deviation',
    count: 4,
    status: 'Review'
  }, {
    id: 2,
    type: 'Referral Trigger',
    count: 9,
    status: 'Review'
  }, {
    id: 3,
    type: 'Limit Breach',
    count: 1,
    status: 'Open'
  }]
}, {
  id: 'umhlanga-retail',
  name: 'Umhlanga Retail Assets Cell',
  category: 'Property',
  sponsor: 'Umhlanga Coastal Properties',
  type: 'Property Programme',
  currency: 'ZAR',
  capitalStatus: 'OK',
  uwResult: '+R12.3M',
  uwResultPositive: true,
  capitalUtilisation: 58.9,
  lrVolatility: 3.1,
  dividendCapacity: 5.2,
  lossTriangle: [{
    year: '2020',
    paid: 70,
    incurred: 80,
    ultimate: 84
  }, {
    year: '2021',
    paid: 55,
    incurred: 72,
    ultimate: 78
  }, {
    year: '2022',
    paid: 30,
    incurred: 58,
    ultimate: 68
  }, {
    year: '2023',
    paid: 12,
    incurred: 42,
    ultimate: 58
  }, {
    year: '2024',
    paid: 3,
    incurred: 22,
    ultimate: 42
  }],
  capitalBuffer: [{
    month: 'Q1',
    required: 90,
    available: 160
  }, {
    month: 'Q2',
    required: 92,
    available: 165
  }, {
    month: 'Q3',
    required: 95,
    available: 162
  }, {
    month: 'Q4',
    required: 98,
    available: 158
  }],
  claimsSummary: [{
    type: 'Open',
    count: 28,
    value: 'R15M'
  }, {
    type: 'Large (>R1.5M)',
    count: 1,
    value: 'R4M'
  }, {
    type: 'Litigated',
    count: 1,
    value: 'R1.5M'
  }],
  underwritingExceptions: [{
    id: 1,
    type: 'Rate Deviation',
    count: 0,
    status: 'OK'
  }, {
    id: 2,
    type: 'Referral Trigger',
    count: 1,
    status: 'Approved'
  }, {
    id: 3,
    type: 'Limit Breach',
    count: 0,
    status: 'OK'
  }]
}];
const COLORS = {
  paid: '#22C55E',
  incurred: '#F59E0B',
  ultimate: '#EC4899',
  required: '#9333EA',
  available: '#3B82F6'
};
export function CaptiveCellPerformance({
  onNavigate
}: CaptiveCellPerformanceProps) {
  const [selectedCellId, setSelectedCellId] = useState(cells[0].id);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const cell = cells.find(c => c.id === selectedCellId) || cells[0];
  const capitalStatusColors = {
    OK: 'bg-green-100 text-green-800',
    Watch: 'bg-amber-100 text-amber-800',
    Breach: 'bg-red-100 text-red-800'
  };
  return <div className="space-y-6 animate-in" data-id="element-317">
      {/* Cell Selector */}
      <div className="relative" data-id="element-318">
        <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="w-full bg-white px-4 py-3 rounded-lg border border-neutral-200 shadow-sm flex items-center justify-between hover:border-pink-300 transition-colors" data-id="element-319">
          <div className="flex items-center gap-3" data-id="element-320">
            <div className="w-2 h-2 rounded-full bg-pink-500" data-id="element-321" />
            <div className="text-left" data-id="element-322">
              <p className="text-sm font-medium text-black" data-id="element-323">{cell.name}</p>
              <p className="text-xs text-neutral-400" data-id="element-324">{cell.category}</p>
            </div>
          </div>
          <ChevronDown size={18} className={`text-neutral-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-325" />
        </button>

        {isDropdownOpen && <>
            <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)} data-id="element-326" />
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg border border-neutral-200 shadow-lg z-20 overflow-hidden" data-id="element-327">
              <div className="p-2" data-id="element-328">
                <p className="px-3 py-1.5 text-[10px] font-medium text-neutral-400 uppercase tracking-wider" data-id="element-329">
                  Fleet Motor / Affinity
                </p>
                {cells.filter(c => c.category === 'Fleet Motor').map(c => <button key={c.id} onClick={() => {
              setSelectedCellId(c.id);
              setIsDropdownOpen(false);
            }} className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${c.id === selectedCellId ? 'bg-pink-50 text-pink-600 font-medium' : 'text-neutral-700 hover:bg-neutral-50'}`} data-id="element-330">
                      {c.name}
                    </button>)}

                <p className="px-3 py-1.5 mt-2 text-[10px] font-medium text-neutral-400 uppercase tracking-wider border-t border-neutral-100 pt-3" data-id="element-331">
                  Property / Affinity
                </p>
                {cells.filter(c => c.category === 'Property').map(c => <button key={c.id} onClick={() => {
              setSelectedCellId(c.id);
              setIsDropdownOpen(false);
            }} className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${c.id === selectedCellId ? 'bg-pink-50 text-pink-600 font-medium' : 'text-neutral-700 hover:bg-neutral-50'}`} data-id="element-332">
                      {c.name}
                    </button>)}
              </div>
            </div>
          </>}
      </div>

      {/* Header */}
      <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm flex justify-between items-start" data-id="element-333">
        <div data-id="element-334">
          <div className="flex items-center gap-3 mb-2" data-id="element-335">
            <h1 className="text-2xl font-bold text-black" data-id="element-336">{cell.name}</h1>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${capitalStatusColors[cell.capitalStatus]}`} data-id="element-337">
              Capital: {cell.capitalStatus}
            </span>
          </div>
          <div className="flex gap-6 text-sm text-neutral-500" data-id="element-338">
            <span data-id="element-339">Sponsor: {cell.sponsor}</span>
            <span data-id="element-340">Type: {cell.type}</span>
            <span data-id="element-341">Currency: {cell.currency}</span>
          </div>
        </div>
        <div className="text-right" data-id="element-342">
          <p className="text-sm text-neutral-500" data-id="element-343">Underwriting Result (YTD)</p>
          <p className={`text-2xl font-bold ${cell.uwResultPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-344">
            {cell.uwResult}
          </p>
        </div>
      </div>

      {/* Cell KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-345">
        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-346">
          <div className="flex justify-between items-start mb-2" data-id="element-347">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-348">
              Capital Utilisation
            </p>
            <Percent size={16} className="text-purple-500" data-id="element-349" />
          </div>
          <h3 className="text-2xl font-bold text-black" data-id="element-350">
            {cell.capitalUtilisation}%
          </h3>
          <p className="text-[10px] text-neutral-400 mt-1" data-id="element-351">
            Required Capital / Available Capital
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-352">
          <div className="flex justify-between items-start mb-2" data-id="element-353">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-354">
              LR Volatility (12m)
            </p>
            <BarChart3 size={16} className="text-pink-500" data-id="element-355" />
          </div>
          <h3 className="text-2xl font-bold text-black" data-id="element-356">
            {cell.lrVolatility}σ
          </h3>
          <p className="text-[10px] text-neutral-400 mt-1" data-id="element-357">
            Std Dev of Loss Ratio (Rolling 12 Months)
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-358">
          <div className="flex justify-between items-start mb-2" data-id="element-359">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-360">
              Dividend Capacity
            </p>
            <Coins size={16} className="text-green-500" data-id="element-361" />
          </div>
          <h3 className="text-2xl font-bold text-black" data-id="element-362">
            R{cell.dividendCapacity}M
          </h3>
          <p className="text-[10px] text-neutral-400 mt-1" data-id="element-363">Surplus – Buffer</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-364">
        {/* Left Column */}
        <div className="space-y-6" data-id="element-365">
          {/* Loss Triangle */}
          <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-366">
            <h3 className="text-lg font-medium text-black mb-4" data-id="element-367">
              Loss Development (Triangle View)
            </h3>
            <div className="h-72" data-id="element-368">
              <ResponsiveContainer width="100%" height="100%" data-id="element-369">
                <ComposedChart data={cell.lossTriangle} data-id="element-370">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-371" />
                  <XAxis dataKey="year" data-id="element-372" />
                  <YAxis unit="%" data-id="element-373" />
                  <Tooltip data-id="element-374" />
                  <Legend data-id="element-375" />
                  <Bar dataKey="paid" name="Paid %" fill={COLORS.paid} barSize={20} data-id="element-376" />
                  <Bar dataKey="incurred" name="Incurred %" fill={COLORS.incurred} barSize={20} data-id="element-377" />
                  <Line type="monotone" dataKey="ultimate" name="Ult. Projection" stroke={COLORS.ultimate} strokeWidth={2} data-id="element-378" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Claims Summary */}
          <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-379">
            <div className="flex justify-between items-center mb-4" data-id="element-380">
              <h3 className="text-lg font-medium text-black" data-id="element-381">Claims Summary</h3>
              <button onClick={() => onNavigate?.('Claims & Governance')} className="text-sm text-pink-600 hover:text-pink-700 font-medium flex items-center gap-1" data-id="element-382">
                View Claims Dashboard <ArrowRight size={14} data-id="element-383" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4" data-id="element-384">
              {cell.claimsSummary.map((item, idx) => <div key={idx} className="p-4 bg-neutral-50 rounded-lg" data-id="element-385">
                  <p className="text-sm text-neutral-500" data-id="element-386">{item.type}</p>
                  <p className="text-xl font-bold text-black mt-1" data-id="element-387">
                    {item.count}
                  </p>
                  <p className="text-xs text-neutral-400 mt-1" data-id="element-388">
                    Value: {item.value}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6" data-id="element-389">
          {/* Capital Buffer */}
          <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-390">
            <h3 className="text-lg font-medium text-black mb-4" data-id="element-391">
              Capital Buffer vs Trigger
            </h3>
            <div className="h-72" data-id="element-392">
              <ResponsiveContainer width="100%" height="100%" data-id="element-393">
                <ComposedChart data={cell.capitalBuffer} data-id="element-394">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-395" />
                  <XAxis dataKey="month" data-id="element-396" />
                  <YAxis data-id="element-397" />
                  <Tooltip data-id="element-398" />
                  <Legend data-id="element-399" />
                  <Area type="monotone" dataKey="available" name="Available Capital" fill="#DBEAFE" stroke={COLORS.available} data-id="element-400" />
                  <Line type="step" dataKey="required" name="Regulatory Trigger" stroke={COLORS.required} strokeWidth={2} strokeDasharray="5 5" data-id="element-401" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Underwriting Exceptions */}
          <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-402">
            <div className="flex justify-between items-center mb-4" data-id="element-403">
              <h3 className="text-lg font-medium text-black" data-id="element-404">
                Underwriting Exceptions
              </h3>
              <button onClick={() => onNavigate?.('Underwriting & Pricing')} className="text-sm text-pink-600 hover:text-pink-700 font-medium flex items-center gap-1" data-id="element-405">
                View Underwriting <ArrowRight size={14} data-id="element-406" />
              </button>
            </div>
            <div className="space-y-3" data-id="element-407">
              {cell.underwritingExceptions.map(item => <div key={item.id} className="flex items-center justify-between p-3 border border-neutral-100 rounded-lg" data-id="element-408">
                  <div className="flex items-center gap-3" data-id="element-409">
                    {item.count > 0 ? <AlertTriangle size={18} className="text-amber-500" data-id="element-410" /> : <CheckCircle size={18} className="text-green-500" data-id="element-411" />}
                    <span className="text-sm font-medium text-black" data-id="element-412">
                      {item.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-413">
                    <span className="text-sm text-neutral-600" data-id="element-414">
                      {item.count} cases
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${item.status === 'OK' ? 'bg-green-100 text-green-800' : item.status === 'Approved' ? 'bg-blue-100 text-blue-800' : item.status === 'Open' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-415">
                      {item.status}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}